// src/firebase/firebaseHelpers.js

import { db } from "../firebase.js";
import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDocs,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

const R2_WORKER_URL = process.env.REACT_APP_R2_WORKER_URL;
// e.g. https://plm-upload.yourname.workers.dev/upload

const R2_CDN_BASE = process.env.REACT_APP_R2_CDN_BASE;
// e.g. https://assets.yourdomain.com

export async function saveStep(supplierId, orderId, docId, data) {
  const ref_ = doc(
    db,
    "users",
    supplierId,
    "orders",
    orderId,
    "workflowSteps",
    docId,
  );

  await setDoc(
    ref_,
    { ...data, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

export async function logActivity(
  supplierId,
  orderId,
  stepId,
  action,
  detail = "",
) {
  await addDoc(
    collection(db, "users", supplierId, "orders", orderId, "activityLogs"),
    {
      stepId,
      productId: supplierId,
      orderId,
      action,
      detail,
      createdAt: serverTimestamp(),
    },
  );
}

export async function uploadDocument(
  supplierId,
  orderId,
  file,
  stepId,
  categoryId,
  tag,
  onProgress,
) {
  const path = `products/${supplierId}/${orderId}/${categoryId}/${stepId}/${Date.now()}_${file.name}`;

  // ── Upload file to R2 via Cloudflare Worker ──────────────────────────────
  const formData = new FormData();
  formData.append("file", file);
  formData.append("path", path);

  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Worker upload failed: ${xhr.status} ${xhr.responseText}`));
      }
    });

    xhr.addEventListener("error", () => reject(new Error("Network error during upload")));
    xhr.addEventListener("abort", () => reject(new Error("Upload aborted")));

    xhr.open("POST", R2_WORKER_URL);
    xhr.send(formData);
  });

  // ── Construct the public CDN URL ─────────────────────────────────────────
  const url = `${R2_CDN_BASE}/${path}`;

  // ── Save metadata to Firestore (unchanged) ───────────────────────────────
  const docRef = await addDoc(
    collection(db, "users", supplierId, "orders", orderId, "documents"),
    {
      productId: supplierId,
      orderId,
      stepId,
      categoryId,
      name: file.name,
      url,
      size: file.size,
      tag: tag || "Other",
      uploadedAt: serverTimestamp(),
      storagePath: path,
    },
  );

  await logActivity(
    supplierId,
    orderId,
    stepId,
    "Document uploaded",
    file.name,
  );

  return docRef.id;
}

// ── Batch-delete a subcollection in chunks of 500 ───────────────────────────
async function deleteSubcollection(supplierId, orderId, subcollectionName) {
  const colRef = collection(
    db,
    "users",
    supplierId,
    "orders",
    orderId,
    subcollectionName,
  );

  const snap = await getDocs(colRef);

  if (snap.empty) return;

  const chunks = [];
  let current = [];

  snap.docs.forEach((d) => {
    current.push(d);
    if (current.length === 500) {
      chunks.push(current);
      current = [];
    }
  });

  if (current.length > 0) chunks.push(current);

  await Promise.all(
    chunks.map((chunk) => {
      const batch = writeBatch(db);
      chunk.forEach((d) => batch.delete(d.ref));
      return batch.commit();
    }),
  );
}

// ── Archive an order ─────────────────────────────────────────────────────────
export async function archiveOrder(supplierId, orderId) {
  const orderRef = doc(db, "users", supplierId, "orders", orderId);

  await updateDoc(orderRef, {
    archived: true,
    archivedAt: serverTimestamp(),
  });

  await Promise.all([
    deleteSubcollection(supplierId, orderId, "workflowSteps"),
    deleteSubcollection(supplierId, orderId, "activityLogs"),
  ]);
}

// ── Cancel an order ──────────────────────────────────────────────────────────
export async function cancelOrder(supplierId, orderId) {
  const orderRef = doc(db, "users", supplierId, "orders", orderId);

  await updateDoc(orderRef, {
    cancelled: true,
    cancelledAt: serverTimestamp(),
  });

  await Promise.all([
    deleteSubcollection(supplierId, orderId, "workflowSteps"),
    deleteSubcollection(supplierId, orderId, "activityLogs"),
  ]);
}